import { Link } from "@tanstack/react-router";
import { Building2, LayoutGrid, List, Plus, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { NewProjectDialog } from "@/components/projects/NewProjectDialog";
import { ProjectProgressBar } from "@/components/projects/ProjectProgressBar";
import { ProjectsDataTable, type ProjectsSortField } from "@/components/projects/ProjectsDataTable";
import { ProjectsKpiCards } from "@/components/projects/ProjectsKpiCards";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import type { Milestone } from "@/hooks/use-milestones";
import { useAllProjectMilestones } from "@/hooks/use-milestones";
import type { Project, ProjectLifecyclePhase } from "@/hooks/use-projects";
import { PROJECT_PHASE_LABELS, useProjects } from "@/hooks/use-projects";
import { cn } from "@/lib/utils";

const PROJECT_TYPE_LABELS: Record<string, string> = {
  general_contracting: "General Contracting",
  cmar: "CMAR",
  design_build: "Design-Build",
  owner_managed: "Owner Managed",
};

function formatCurrency(value: string | null): string {
  if (!value) return "--";
  const num = Number(value);
  if (Number.isNaN(num)) return "--";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(num);
}

function statusVariant(
  status: string,
): "success" | "warning" | "secondary" | "destructive" | "default" {
  switch (status) {
    case "active":
      return "success";
    case "draft":
    case "pending":
      return "warning";
    case "completed":
    case "closed":
      return "secondary";
    case "cancelled":
    case "on_hold":
      return "destructive";
    default:
      return "default";
  }
}

function phaseBadgeClass(phase: ProjectLifecyclePhase): string {
  switch (phase) {
    case "pre_construction":
      return "border-transparent bg-amber-500/15 text-amber-700";
    case "design":
      return "border-transparent bg-sky-500/15 text-sky-700";
    case "construction":
      return "border-transparent bg-blue-500/15 text-blue-700";
    case "post_construction":
      return "border-transparent bg-violet-500/15 text-violet-700";
    case "completed":
      return "border-transparent bg-gray-400/20 text-gray-600";
  }
}

type ProjectFilter =
  | { label: string; kind: "all" }
  | { label: string; kind: "status"; value: "active" | "on_hold" | "completed" }
  | { label: string; kind: "phase"; value: ProjectLifecyclePhase };

const PROJECT_FILTERS = [
  { label: "All", kind: "all" },
  { label: "Active", kind: "status", value: "active" },
  { label: "On Hold", kind: "status", value: "on_hold" },
  { label: "Pre-Construction", kind: "phase", value: "pre_construction" },
  { label: "Construction", kind: "phase", value: "construction" },
  { label: "Post-Construction", kind: "phase", value: "post_construction" },
  { label: "Completed", kind: "phase", value: "completed" },
] as const satisfies readonly ProjectFilter[];

function filterKey(f: ProjectFilter): string {
  return f.kind === "all" ? "all" : `${f.kind}:${f.value}`;
}

function matchesFilter(project: Project, f: ProjectFilter): boolean {
  if (f.kind === "all") return true;
  if (f.kind === "status") return project.status === f.value;
  return project.phase === f.value;
}

type SortField = ProjectsSortField | "updatedAt";
type SortDir = "asc" | "desc";
type ViewMode = "grid" | "list";

const VIEW_MODE_STORAGE_KEY = "projects:viewMode";

function readViewMode(): ViewMode {
  if (typeof window === "undefined") return "grid";
  const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
  return stored === "list" ? "list" : "grid";
}

const SORT_OPTIONS: { value: SortField; label: string }[] = [
  { value: "name", label: "Name" },
  { value: "contractValue", label: "Contract value" },
];

function compareProjects(a: Project, b: Project, field: SortField, dir: SortDir): number {
  let cmp = 0;
  switch (field) {
    case "name":
      cmp = a.name.localeCompare(b.name);
      break;
    case "type":
      cmp = (a.type ?? "").localeCompare(b.type ?? "");
      break;
    case "location":
      cmp = (a.location ?? "").localeCompare(b.location ?? "");
      break;
    case "status":
      cmp = a.status.localeCompare(b.status);
      break;
    case "phase":
      cmp = a.phase.localeCompare(b.phase);
      break;
    case "contractValue": {
      const an = a.contractValue ? Number(a.contractValue) : 0;
      const bn = b.contractValue ? Number(b.contractValue) : 0;
      cmp = an - bn;
      break;
    }
    case "updatedAt":
      // Fallback — no updatedAt on Project type here; sort by name for stability.
      cmp = a.name.localeCompare(b.name);
      break;
  }
  return dir === "asc" ? cmp : -cmp;
}

export function ProjectsPage() {
  const [wizardOpen, setWizardOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<ProjectFilter>(PROJECT_FILTERS[0]);
  const [pmFilter, setPmFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [viewMode, setViewMode] = useState<ViewMode>(() => readViewMode());
  const { data: projects, isLoading, isError, error, refetch } = useProjects();
  const { data: allMilestones } = useAllProjectMilestones();

  useEffect(() => {
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  const milestonesByProject = useMemo(() => {
    return (allMilestones ?? []).reduce<Record<string, Milestone[]>>((acc, m) => {
      const bucket = acc[m.projectId] ?? [];
      bucket.push(m);
      acc[m.projectId] = bucket;
      return acc;
    }, {});
  }, [allMilestones]);

  const allProjects = useMemo(() => projects ?? [], [projects]);

  const pmOptions = useMemo(() => {
    const names = allProjects
      .map((p) => p.projectManager)
      .filter((pm): pm is string => typeof pm === "string" && pm.length > 0);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  }, [allProjects]);

  const filterCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const f of PROJECT_FILTERS) {
      counts[filterKey(f)] = allProjects.filter((p) => matchesFilter(p, f)).length;
    }
    return counts;
  }, [allProjects]);

  const filteredProjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = allProjects.filter((project) => {
      if (!matchesFilter(project, activeFilter)) return false;
      if (pmFilter !== "all" && project.projectManager !== pmFilter) return false;
      if (q === "") return true;
      const haystack = [
        project.name,
        project.projectNumber,
        project.location,
        project.client,
        project.projectManager,
      ]
        .filter((v): v is string => typeof v === "string" && v.length > 0)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
    return [...filtered].sort((a, b) => compareProjects(a, b, sortField, sortDir));
  }, [allProjects, activeFilter, pmFilter, search, sortField, sortDir]);

  const handleTableSort = (field: ProjectsSortField) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">{error?.message ?? "Failed to load projects"}</p>
        <Button variant="outline" onClick={() => refetch()} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Projects</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Portfolio overview across all delivery methods and phases.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setWizardOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Project
          </Button>
          <NewProjectDialog open={wizardOpen} onOpenChange={setWizardOpen} />
        </div>
      </div>

      {/* KPIs */}
      <ProjectsKpiCards projects={allProjects} milestones={allMilestones ?? []} />

      {/* Filter bar */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {PROJECT_FILTERS.map((f) => {
              const key = filterKey(f);
              const active = filterKey(activeFilter) === key;
              return (
                <Button
                  key={key}
                  variant={active ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActiveFilter(f)}
                  className="gap-2"
                >
                  <span>{f.label}</span>
                  <span
                    className={cn(
                      "rounded-md px-1.5 py-0.5 text-[10px] font-semibold",
                      active ? "bg-background/20 text-inherit" : "bg-muted text-muted-foreground",
                    )}
                  >
                    {filterCounts[key] ?? 0}
                  </span>
                </Button>
              );
            })}
          </div>
          {/* Search + sort controls
              Mobile:  search full-width on top row, PM/sort/view on second row
              sm+:     single row with fixed widths */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
            {/* Search — full width on mobile, fixed width on sm+ */}
            <div className="relative sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search projects…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* PM + Sort + view toggle — share a row on mobile */}
            <div className="flex items-center gap-2">
              <Select value={pmFilter} onValueChange={setPmFilter}>
                <SelectTrigger className="flex-1 sm:w-[140px] sm:flex-none min-w-0" size="sm">
                  <SelectValue placeholder="All PMs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All PMs</SelectItem>
                  {pmOptions.map((pm) => (
                    <SelectItem key={pm} value={pm}>
                      {pm}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={sortField}
                onValueChange={(v) => {
                  setSortField(v as SortField);
                  setSortDir("asc");
                }}
              >
                <SelectTrigger className="flex-1 sm:w-[150px] sm:flex-none min-w-0" size="sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      Sort: {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="inline-flex shrink-0 items-center rounded-md border bg-background p-0.5">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "inline-flex h-7 w-7 items-center justify-center rounded-sm",
                    viewMode === "grid"
                      ? "bg-foreground text-background"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                  aria-label="Grid view"
                  aria-pressed={viewMode === "grid"}
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "inline-flex h-7 w-7 items-center justify-center rounded-sm",
                    viewMode === "list"
                      ? "bg-foreground text-background"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                  aria-label="List view"
                  aria-pressed={viewMode === "list"}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Showing <span className="font-semibold text-foreground">{filteredProjects.length}</span>{" "}
          of {allProjects.length} projects
        </p>
      </div>

      {/* Body */}
      {filteredProjects.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Building2 className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
            <p>No projects match your filters.</p>
          </CardContent>
        </Card>
      ) : viewMode === "grid" ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredProjects.map((project) => (
            <Link
              key={project.id}
              to="/projects/$projectId"
              params={{ projectId: project.id }}
              search={{ tab: "overview" }}
            >
              <Card className="border-border/50 hover:border-primary/30 transition-colors h-full">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base font-semibold leading-tight">
                      {project.name}
                    </CardTitle>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className={phaseBadgeClass(project.phase)}>
                        {PROJECT_PHASE_LABELS[project.phase]}
                      </Badge>
                      <Badge variant={statusVariant(project.status)}>{project.status}</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Delivery Method</span>
                    <span className="font-medium text-foreground">
                      {PROJECT_TYPE_LABELS[project.type] ?? project.type ?? "--"}
                    </span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Location</span>
                    <span className="font-medium text-foreground">{project.location ?? "--"}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Contract Value</span>
                    <span className="font-medium text-foreground">
                      {formatCurrency(project.contractValue)}
                    </span>
                  </div>
                  <ProjectProgressBar milestones={milestonesByProject[project.id] ?? []} />
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <ProjectsDataTable
          projects={filteredProjects}
          milestonesByProject={milestonesByProject}
          sortField={sortField === "updatedAt" ? "name" : (sortField as ProjectsSortField)}
          sortDir={sortDir}
          onSortChange={handleTableSort}
        />
      )}
    </div>
  );
}
